export const helpCenterFaqsIntro =
	"Find answers to common questions about using EX-ACT, from getting started to troubleshooting and reporting.";

export type FaqItem = { question: string; answer: string };

export type FaqGroup = {
	id: string;
	title: string;
	items: FaqItem[];
};

export const helpCenterFaqGroups: FaqGroup[] = [
	{
		id: "general",
		title: "General Information",
		items: [
			{
				question: "What is EX-ACT?",
				answer:
					"The Environmental eXternalities ACcounting Tool (EX-ACT) is an FAO-developed tool that estimates greenhouse gas (GHG) emissions and sequestration across agrifood systems. It covers AFOLU sector emissions, post-harvest to retail emissions, and value chain-related sources such as transport, processing, inputs, and irrigation. EX-ACT is recognized and used by FAO, IFAD, the World Bank, GEF, GCF, and other international partners for climate accounting in agricultural investment projects.",
			},
			{
				question: "When can EX-ACT be used?",
				answer:
					"EX-ACT can be used when there is a need to quantify the environmental externalities of a specific investment, policy, or project in agrifood systems. The tool can be applied at any stage: ex ante (design / proposal) to estimate mitigation potential, mid-term to update monitoring for implementation, ex post to quantify achieved mitigation results.",
			},
			{
				question:
					"Is EX-ACT recognized by climate finance institutions such as the GCF and GEF?",
				answer:
					"Yes. Both GCF and GEF recognize and actively use EX-ACT for project GHG accounting, monitoring, and reporting.",
			},
			{
				question: "What organizations and institutions currently use EX-ACT?",
				answer:
					"EX-ACT is used across Major International Finance Institutions and Multilateral Development Banks, including: The World Bank, IFAD, ADB, GCF, GEF. It is also used within FAO and by National Ministries to account for country-level investment plans under the Hand In Hand Initiative, as well as for project appraisals. Last but not least, it is used by research institutions including ILRI and CGIAR for stakeholder support and climate mitigation quantification.",
			},
			{
				question: "Can EX-ACT be used by the private sector (e.g., CSR initiatives)?",
				answer:
					"Yes, EX-ACT is free to use in accordance with the Terms and Conditions on the application website. Users are responsible for ensuring compliance with FAO legal frameworks.",
			},
		],
	},
	{
		id: "access",
		title: "Access and Learning Support",
		items: [
			{
				question: "Is there a user manual or training material?",
				answer:
					"User manual, guidance materials, and training content are under development. For support, contact: ex-act@fao.org",
			},
			{
				question: "Can I receive training on how to use EX-ACT?",
				answer:
					"Yes. Training opportunities can be arranged upon request with the EX-ACT team. Trainings are offered against coverage of preparation, delivery and follow-up expenses.",
			},
		],
	},
	{
		id: "technical",
		title: "Technical Functionality",
		items: [
			{
				question: "Can EX-ACT integrate Tier 2 or Tier 3 emission factors?",
				answer:
					"Yes. EX-ACT includes Tier 1 IPCC default factors but allows integration of Tier 2 and Tier 3 data, whenever available, through the dedicated \"Tier 2\" section of the tool.",
			},
			{
				question: "How does EX-ACT handle additionality, permanence, and leakage?",
				answer:
					"These aspects are context-specific and EX-ACT does not automatically assess additionality, permanence, or leakage, as these depend on policy context, behavioural responses, implementation conditions and data availability. These dimensions must be evaluated through complementary assessment frameworks and project-level analysis and then reflected in EX-ACT (e.g. adoption rates, areas, activity design, or conservative assumptions). FAO is developing further guidance to help users systematically integrate these considerations when using EX-ACT.",
			},
			{
				question: "Is there a minimum target land size required for using EX-ACT?",
				answer:
					"No minimum is required. Default emission factors are, however, typically defined on a hectare/head basis.",
			},
			{
				question: "Can EX-ACT integrate geospatial data?",
				answer:
					"Yes, EX-ACT uses geographic inputs to determine climate and moisture conditions. Users can upload JSON, KMZ, zipped GeoJSON files by drag and drop anywhere on the map to proceed with analysis.",
			},
			{
				question: "Can EX-ACT assess biodiversity or just climate mitigation?",
				answer:
					"Yes. EX-ACT supports biodiversity impact assessment alongside climate mitigation. Define a project area baseline on the map, then create Biodiversity Assessment activities to quantify impacts using Mean Species Abundance (MSA) indicators.",
			},
			{
				question: "Can EX-ACT export data directly to IPCC reporting software?",
				answer:
					"No. The tool produces a downloadable narrative report and an Excel file, but results align with ETF and IPCC methodologies and can be manually transposed.",
			},
		],
	},
	{
		id: "applications",
		title: "Applications and Use Cases",
		items: [
			{
				question: "Can EX-ACT account for ecosystem restoration activities?",
				answer:
					"Yes. EX-ACT can assess a wide range of ecosystem restoration activities, including reforestation, afforestation, assisted natural regeneration, wetland and peatland restoration, and rehabilitation of degraded lands. By quantifying changes in biomass, soil carbon, and associated emissions over time, EX-ACT estimates the mitigation benefits of restoration interventions across the accounting period.",
			},
			{
				question: "Can EX-ACT account for regenerative agriculture practices?",
				answer:
					"Yes. Many regenerative agriculture practices can be reflected in EX-ACT through changes in land management parameters and activity design. This includes practices such as reduced tillage, cover cropping, improved crop rotations, residue management, and enhanced soil fertility management. These practices are captured through effects on soil organic carbon, input use, and emissions from agricultural systems.",
			},
			{
				question: "Can EX-ACT account for improved forest management?",
				answer:
					"Yes. EX-ACT can account for a variety of improved forest management interventions, including sustainable forest management, forest protection, restoration activities within forest areas, fire management, and enrichment planting. The tool estimates their impact on forest carbon stocks and related emissions or removals over time, supporting assessment of mitigation outcomes.",
			},
			{
				question: "Can EX-ACT be applied in urban contexts?",
				answer:
					"Yes. While EX-ACT is primarily designed for agrifood and land-use interventions, it can also be applied in urban and peri-urban settings, particularly for assessing urban forests, green spaces, and settlement areas (infrastructure and roads). The tool includes a dedicated Settlement module, which allows users to account for land-use change, biomass loss or gain, and emissions associated with settlement expansion. Given the complexity and variability of urban environments, the use of Tier 2 or context-specific data is strongly recommended to ensure credible results.",
			},
			{
				question: "Can EX-ACT estimate emissions related to Food Loss and Waste?",
				answer:
					"Yes, EX-ACT can be used to estimate emissions associated with Food Loss and Waste, although this requires some additional methodological inputs. The general approach is to account for the extra agricultural production and resource use that would be needed to supply the food that is lost along the value chain or wasted at consumption. By quantifying losses at different stages and linking them to production systems, EX-ACT can reflect the associated emissions from land use, inputs, energy, and supply-chain processes. Users should therefore complement EX-ACT with reliable FLW data and assumptions on loss rates, system boundaries, and relevant stages of the agrifood chain to ensure robust estimates.",
			},
			{
				question: "Can EX-ACT be used for carbon markets or trading?",
				answer:
					"Not on its own. EX‑ACT, as a tool, is not intended for use in carbon markets or trading. Market‑based mechanisms require validated methodologies that rigorously address additionality, permanence, leakage, monitoring and verification (MRV), and uncertainty - elements that go beyond EX‑ACT's core design. The EX‑ACT team is currently developing complementary guidance to help practitioners assess additionality, permanence, and uncertainty within EX‑ACT assessments. These efforts strengthen the robustness of EX‑ACT estimates, but any use for carbon markets must still rely on recognised certification standards and verification frameworks.",
			},
		],
	},
	{
		id: "results",
		title: "Results and reporting",
		items: [
			{
				question: "How can EX-ACT outputs support GCF and GEF reporting?",
				answer:
					"EX‑ACT provides structured results (tCO₂‑eq per activity, per year, and for total project lifetime) that can be directly inserted into GCF APR mitigation tables and GEF Core Indicator 6 (GHG emissions). Use the downloadable reports or the module‑level tables to fill in required fields such as emission reductions, baseline vs project emissions, and lifetime mitigation impact.",
			},
			{
				question: "Can EX-ACT results be used for national reporting (NDCs, BURs, ETF)?",
				answer:
					"Yes. EX‑ACT uses IPCC‑aligned emission factors and methodologies, making its outputs compatible with national MRV systems. While EX‑ACT does not replace national inventory models, project‑level mitigation estimates can feed into NDC tracking, BURs, and climate‑finance reporting when aligned with national data and definitions. Use the downloadable reports to fill in required fields according to the NDC reporting structures (such as the Enhanced Transparency Framework).",
			},
			{
				question: "Why has my carbon balance changed since last login?",
				answer:
					"Your carbon balance may change because EX-ACT is continuously updated to improve its methodologies, integrate new scientific evidence, and fix technical issues. These updates ensure that results stay fully aligned with reality and with the latest IPCC guidelines. If you need your project's carbon balance to remain fixed and not update with new releases, you can finalize the project directly from the project page. For further guidance, please refer to the EX-ACT user manual.",
			},
			{
				question: "How do I compare results across different projects?",
				answer:
					"EX-ACT versions may differ because methodologies, emission factors, or module structures evolve. To compare versions, rerun the same project in each version and review the Summary and Module pages side by side. Focus on parameters that changed across versions - such as updated defaults or revised forest and livestock modules - since these typically explain differences in carbon balances. In case of doubt, you can always contact us at ex-act@fao.org.",
			},
		],
	},
	{
		id: "troubleshooting",
		title: "Troubleshooting",
		items: [
			{
				question: "The tool is not giving project results, what should I do?",
				answer:
					"Verify that:\n• All activities are fully completed, and especially the land use change module – since its computations are dependent on initial and final land uses, it may sometimes not calculate results.\n• No fields are left incomplete\n• Each activity shows a green completion check (✓)\nOnce all activities are finalized, the tool will run calculations. If issues persist, contact the EX-ACT team at ex-act@fao.org",
			},
			{
				question: "What should I do if my results seem unexpectedly high or low?",
				answer:
					"First, double-check activity data (e.g., surfaces, yields, livestock numbers) and ensure the baseline and project scenarios are correctly defined. Then review whether the input data were inserted, and all the correct parameters were selected. If results still look unusual, try running the project in a previous version or consult the relevant module guidance to identify data entry issues or updated methodologies.\nIf still uncertain, contact the EX-ACT team at ex-act@fao.org",
			},
			{
				question: "The page or project is not loading; how to fix this?",
				answer:
					"Try:\n1. Hard refresh\n   o Windows: Ctrl + F5\n   o Mac: Command + R\n2. Check internet stability\n3. Try different browser\nIf unresolved, send project ID, screenshots, and browser details to ex-act@fao.org.",
			},
			{
				question: "How do I report bugs or get support?",
				answer:
					"Email ex-act@fao.org with project ID, browser type, screenshots, and a short description of the issue.",
			},
		],
	},
];
